// frontend/src/components/meetings/MeetingDashboard.jsx
import React, { useState, useEffect } from "react";
import {
  Plus,
  Calendar,
  Video,
  FileText,
  Download,
  Search,
  Filter,
} from "lucide-react";
import api from "../../services/api";
import MeetingScheduler from "./MeetingScheduler";
import MeetingDetails from "./MeetingDetails";
import { toast } from "react-toastify";

const MeetingDashboard = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    platform: "all",
    search: "",
  });

  useEffect(() => {
    fetchMeetings();
  }, [filters]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await api.getMeetings(filters);
      setMeetings(response.meetings);
    } catch (error) {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  const handleStopRecording = async (meetingId) => {
    try {
      await api.stopRecording(meetingId);
      toast.success("Recording stopped");
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to stop recording");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { bg: "bg-blue-600", text: "Scheduled" },
      joining: { bg: "bg-yellow-600", text: "Joining" },
      recording: { bg: "bg-red-600", text: "Recording" },
      transcribing: { bg: "bg-purple-600", text: "Transcribing" },
      processing: { bg: "bg-orange-600", text: "Processing" },
      completed: { bg: "bg-green-600", text: "Completed" },
      failed: { bg: "bg-gray-600", text: "Failed" },
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${config.bg}`}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Meeting Recordings</h1>
          <p className="text-gray-400">
            Manage and view all your meeting recordings
          </p>
        </div>
        <button
          onClick={() => setShowScheduler(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
        >
          <Plus size={20} />
          Schedule Recording
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search meetings..."
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            className="w-full pl-10 pr-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, status: e.target.value }))
          }
          className="px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="recording">Recording</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>

        <select
          value={filters.platform}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, platform: e.target.value }))
          }
          className="px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
        >
          <option value="all">All Platforms</option>
          <option value="zoom">Zoom</option>
          <option value="teams">Teams</option>
          <option value="meet">Google Meet</option>
        </select>
      </div>

      {/* Meetings List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          Loading meetings...
        </div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-12">
          <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">No meetings found</p>
          <button
            onClick={() => setShowScheduler(true)}
            className="text-purple-400 hover:text-purple-300"
          >
            Schedule your first recording
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {meetings.map((meeting) => (
            <div
              key={meeting.id}
              className="bg-[#13161B] border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={`/assets/${meeting.platform}.png`}
                      alt={meeting.platform}
                      className="w-6 h-6"
                    />
                    <h3 className="text-lg font-semibold">
                      {meeting.title || "Untitled Meeting"}
                    </h3>
                    {getStatusBadge(meeting.status)}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(meeting.scheduledAt).toLocaleDateString()}
                    </span>
                    <span>
                      {new Date(meeting.scheduledAt).toLocaleTimeString()}
                    </span>
                    {meeting.duration && (
                      <span>{Math.floor(meeting.duration / 60)} min</span>
                    )}
                  </div>

                  {meeting.insights?.summary && (
                    <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                      {meeting.insights.summary}
                    </p>
                  )}

                  {meeting.insights?.tags && (
                    <div className="flex gap-2 flex-wrap">
                      {meeting.insights.tags.slice(0, 5).map((tag, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-gray-700 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  {meeting.status === "recording" && (
                    <button
                      onClick={() => handleStopRecording(meeting.id)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                      title="Stop Recording"
                    >
                      <div className="w-4 h-4 bg-white rounded-sm" />
                    </button>
                  )}

                  {meeting.status === "completed" && (
                    <>
                      <button
                        onClick={() => setSelectedMeeting(meeting)}
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => api.downloadTranscript(meeting.id)}
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        title="Download Transcript"
                      >
                        <Download size={16} />
                      </button>
                    </>
                  )}

                  {meeting.status === "failed" && (
                    <button
                      onClick={() =>
                        api.retryMeeting(meeting.id).then(fetchMeetings)
                      }
                      className="px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm transition-colors"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showScheduler && (
        <MeetingScheduler
          onClose={() => setShowScheduler(false)}
          onSuccess={() => {
            setShowScheduler(false);
            fetchMeetings();
          }}
        />
      )}

      {selectedMeeting && (
        <MeetingDetails
          meeting={selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
        />
      )}
    </div>
  );
};

export default MeetingDashboard;
